import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import jobOfferService from "../../services/jobOfferService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormSection from "../../components/ui/FormSection";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import "../../styles/FormCard.css";

const JobOfferEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    department: "",
    contract_type: "",
    skills_required: "",
    experience_level: "",
    deadline: "",
    status: "OPEN",
    number_of_positions: 1,
    publication_platform: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await jobOfferService.getById(id);
      const offer = response?.data || response;
      
      setFormData({
        title: offer.title || "",
        description: offer.description || "",
        location: offer.location || "",
        department: offer.department || "",
        contract_type: offer.contract_type || "",
        skills_required: offer.skills_required || "",
        experience_level: offer.experience_level || "",
        deadline: offer.deadline ? offer.deadline.split('T')[0] : "",
        status: offer.status || "OPEN",
        number_of_positions: offer.number_of_positions || 1,
        publication_platform: offer.publication_platform || "",
      });
    } catch (err) {
      console.error("Error loading job offer:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Le titre est requis";
    if (!formData.description.trim()) newErrors.description = "La description est requise";
    if (!formData.location.trim()) newErrors.location = "La localisation est requise";
    if (!formData.department.trim()) newErrors.department = "Le département est requis";
    if (!formData.contract_type.trim()) newErrors.contract_type = "Le type de contrat est requis";
    if (!formData.skills_required.trim()) newErrors.skills_required = "Les compétences requises sont requises";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      await jobOfferService.update(id, formData);
      toast.success("Offre mise à jour avec succès");
      navigate("/rh/job-offers");
    } catch (err) {
      console.error("Error updating job offer:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/rh/job-offers");
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Offres d'emploi", to: "/rh/job-offers" },
    { label: "Modifier l'offre" }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard title="Modifier l'offre" showBackButton onBack={handleCancel}>
        <form onSubmit={handleSubmit}>
          <FormSection title="Informations générales">
            <div className="form-group">
              <label>Titre *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={errors.title ? "error" : ""}
              />
              {errors.title && <span className="error">{errors.title}</span>}
            </div>
            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={errors.description ? "error" : ""}
              />
              {errors.description && <span className="error">{errors.description}</span>}
            </div>
            <div className="form-group">
              <label>Localisation *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Tunis, Tunisie"
                className={errors.location ? "error" : ""}
              />
              {errors.location && <span className="error">{errors.location}</span>}
            </div>
            <div className="form-group">
              <label>Département *</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                className={errors.department ? "error" : ""}
              />
              {errors.department && <span className="error">{errors.department}</span>}
            </div>
          </FormSection>

          <FormSection title="Détails du poste">
            <div className="form-group">
              <label>Type de contrat *</label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleChange}
                className={errors.contract_type ? "error" : ""}
              >
                <option value="">Sélectionner un type</option>
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="Stage">Stage</option>
                <option value="Freelance">Freelance</option>
                <option value="Alternance">Alternance</option>
              </select>
              {errors.contract_type && <span className="error">{errors.contract_type}</span>}
            </div>
            <div className="form-group">
              <label>Compétences requises *</label>
              <textarea
                name="skills_required"
                value={formData.skills_required}
                onChange={handleChange}
                placeholder="Ex: Python, React, FastAPI, Docker"
                className={errors.skills_required ? "error" : ""}
              />
              {errors.skills_required && <span className="error">{errors.skills_required}</span>}
            </div>
            <div className="form-group">
              <label>Niveau d'expérience</label>
              <select
                name="experience_level"
                value={formData.experience_level}
                onChange={handleChange}
              >
                <option value="">Sélectionner un niveau</option>
                <option value="Junior">Junior (0-2 ans)</option>
                <option value="Mid-level">Mid-level (2-5 ans)</option>
                <option value="Senior">Senior (5+ ans)</option>
                <option value="Expert">Expert (10+ ans)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Nombre de postes</label>
              <input
                type="number"
                name="number_of_positions"
                value={formData.number_of_positions}
                onChange={handleChange}
                min="1"
              />
            </div>
          </FormSection>

          <FormSection title="Paramètres">
            <div className="form-group">
              <label>Date limite</label>
              <input
                type="date"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="OPEN">Ouverte</option>
                <option value="CLOSED">Fermée</option>
                <option value="DRAFT">Brouillon</option>
              </select>
            </div>
            <div className="form-group">
              <label>Plateforme de publication</label>
              <input
                type="text"
                name="publication_platform"
                value={formData.publication_platform}
                onChange={handleChange}
                placeholder="Ex: LinkedIn, Indeed, etc."
              />
            </div>
          </FormSection>

          <FormActions
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveText="Enregistrer les modifications"
            isLoading={submitting}
          />
        </form>
      </FormCard>
    </div>
  );
};

export default JobOfferEdit;

